import { useAuth } from '@/contexts/Auth'
import {
  getDismissedAnnouncementIds,
  subscribeActiveAnnouncements,
  subscribeAnnouncement,
} from '@/services/platform-announcement'
import type { PlatformAnnouncement } from '@/services/platform-announcement/model'
import { Timestamp } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'

const TICK_INTERVAL_MS = 60_000

const toMillis = (value: Timestamp | string): number => {
  if (value instanceof Timestamp) return value.toMillis()
  return new Date(value).getTime()
}

const storageKey = (uid: string) => `platform-announcement-dismissed:${uid}`
const migrationKey = (uid: string) => `platform-announcement-migrated:${uid}`

const loadDismissed = (uid: string): Set<string> => {
  try {
    const raw = localStorage.getItem(storageKey(uid))
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch {
    // ignore corrupt data
  }
  return new Set()
}

const saveDismissed = (uid: string, ids: Set<string>) => {
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify([...ids]))
  } catch {
    // ignore storage errors
  }
}

export const usePlatformAnnouncements = () => {
  const { user } = useAuth()
  const uid = user?.uid
  const [announcements, setAnnouncements] = useState<PlatformAnnouncement[]>([])
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [sessionDismissedIds, setSessionDismissedIds] = useState<Set<string>>(new Set())
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    setSessionDismissedIds(new Set())
    setDismissedIds(uid ? loadDismissed(uid) : new Set())

    if (!uid || localStorage.getItem(migrationKey(uid))) return
    getDismissedAnnouncementIds(uid)
      .then((firestoreIds) => {
        setDismissedIds((prev) => {
          const merged = new Set([...prev, ...firestoreIds])
          saveDismissed(uid, merged)
          return merged
        })
      })
      .catch(() => undefined)
      .finally(() => {
        localStorage.setItem(migrationKey(uid), '1')
      })
  }, [uid])

  useEffect(() => {
    return subscribeActiveAnnouncements(setAnnouncements)
  }, [])

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS)
    return () => window.clearInterval(tick)
  }, [])

  const visible = useMemo(
    () =>
      announcements.filter(
        (a) =>
          !dismissedIds.has(a.id) &&
          !sessionDismissedIds.has(a.id) &&
          toMillis(a.startDate) <= now &&
          toMillis(a.endDate) >= now
      ),
    [announcements, dismissedIds, sessionDismissedIds, now]
  )

  const dismiss = useCallback(
    (announcementId: string, permanent: boolean) => {
      if (permanent) {
        setDismissedIds((prev) => {
          const next = new Set(prev).add(announcementId)
          if (uid) saveDismissed(uid, next)
          return next
        })
      } else {
        setSessionDismissedIds((prev) => new Set(prev).add(announcementId))
      }
    },
    [uid]
  )

  return { announcements: visible, dismiss }
}

export const useAnnouncement = (id: string | undefined) => {
  const [announcement, setAnnouncement] = useState<PlatformAnnouncement | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) {
      setAnnouncement(null)
      setLoading(false)
      return
    }
    setLoading(true)
    return subscribeAnnouncement(id, (next) => {
      setAnnouncement(next)
      setLoading(false)
    })
  }, [id])

  return { announcement, loading }
}
